import { useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, CheckCircle2, Package2, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Very simple CSV parser (handles quoted fields)
function parseCSV(text) {
  const rows = [];
  let row = [], field = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') { inQ = false; }
      else field += c;
    } else {
      if (c === '"') inQ = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else if (c === "\r") { /* skip */ }
      else field += c;
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((x) => x && x.trim() !== ""));
}

const HEADERS = ["nombre", "direccion", "comuna", "telefono", "email", "referencia", "descripcion", "valor_declarado"];

export default function CargaMasiva() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { created, bulto }
  const nav = useNavigate();

  const downloadTemplate = () => {
    const csv = HEADERS.join(",") + "\n" +
      '"Juan Pérez","Av. Providencia 1234","Providencia","+56912345678","juan@example.cl","Timbre 2","Ropa",25000\n' +
      '"María Soto","Calle Los Álamos 567","Ñuñoa","+56987654321","","","Zapatillas",42000';
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "plantilla-voycargo.csv"; a.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseCSV(text);
    if (parsed.length < 2) { toast.error("Archivo vacío"); return; }
    const header = parsed[0].map((h) => h.trim().toLowerCase());
    const missing = ["nombre", "direccion", "comuna"].filter((k) => !header.includes(k));
    if (missing.length) { toast.error("Faltan columnas: " + missing.join(", ")); return; }
    const data = parsed.slice(1).map((r) => {
      const obj = {};
      header.forEach((h, i) => { obj[h] = (r[i] || "").trim(); });
      return obj;
    });
    setRows(data);
    toast.success(`${data.length} filas cargadas — revisa y confirma`);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const orders = rows.map((r) => ({
        destinatario: {
          nombre: r.nombre, direccion: r.direccion, comuna: r.comuna,
          telefono: r.telefono || null, email: r.email || null, referencia: r.referencia || null,
        },
        descripcion: r.descripcion || null,
        valor_declarado: r.valor_declarado ? parseFloat(r.valor_declarado) : null,
      }));
      const res = await api.post("/orders/bulk", { orders });
      toast.success(`${res.data.created} pedidos creados` + (res.data.bulto ? ` · Bulto ${res.data.bulto.code}` : ""));
      setResult(res.data);
      setRows([]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const downloadBultoPDF = () => {
    if (!result?.bulto?.id) return;
    const token = localStorage.getItem("voycargo_token");
    fetch(`${API_BASE}/bultos/${result.bulto.id}/label.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => window.open(URL.createObjectURL(b), "_blank"));
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Carga masiva</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Importar por CSV / Excel</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">Descarga la plantilla, complétala en Excel o Sheets, guárdala como CSV y súbela.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="border border-white/10 p-8 bg-zinc-900/40">
          <div className="flex items-center gap-3 mb-4">
            <FileSpreadsheet className="w-6 h-6 text-yellow-300" />
            <div className="font-display font-bold text-xl">1. Descarga la plantilla</div>
          </div>
          <p className="text-zinc-400 text-sm mb-6">Incluye ejemplos. Campos obligatorios: nombre, dirección, comuna.</p>
          <button onClick={downloadTemplate} className="border border-white/20 hover:border-yellow-300 px-4 py-2.5 text-sm font-mono-tech uppercase tracking-widest inline-flex items-center gap-2" data-testid="download-template">
            <Download className="w-4 h-4" /> Descargar CSV
          </button>
        </div>
        <div className="border border-white/10 p-8 bg-zinc-900/40">
          <div className="flex items-center gap-3 mb-4">
            <Upload className="w-6 h-6 text-yellow-300" />
            <div className="font-display font-bold text-xl">2. Sube el archivo</div>
          </div>
          <p className="text-zinc-400 text-sm mb-6">Formato CSV (guardar como &ldquo;CSV UTF-8&rdquo; en Excel).</p>
          <label className="border border-white/20 hover:border-yellow-300 px-4 py-2.5 text-sm font-mono-tech uppercase tracking-widest inline-flex items-center gap-2 cursor-pointer" data-testid="upload-csv">
            <Upload className="w-4 h-4" /> Seleccionar archivo
            <input type="file" accept=".csv" onChange={onFile} className="hidden" />
          </label>
        </div>
      </div>

      {result && result.bulto && (
        <div className="border border-yellow-300/40 bg-yellow-300/5 p-8 mb-8" data-testid="bulto-created">
          <div className="flex items-center gap-3 mb-4">
            <Package2 className="w-6 h-6 text-yellow-300" />
            <div>
              <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Bulto consolidado creado</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-1">{result.bulto.code}</div>
            </div>
          </div>
          <p className="text-zinc-300 mb-5">
            <b>{result.bulto.count} pedidos</b> agrupados en un solo bulto. El PDF incluye la etiqueta general (con QR grande para pegar en la caja de embalaje) más las {result.bulto.count} etiquetas individuales para cada paquete.
          </p>
          <div className="p-4 bg-zinc-950 border border-white/5 mb-5">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Cómo funciona el bulto</div>
            <ul className="text-sm text-zinc-400 space-y-1 font-mono-tech">
              <li>1. Pegá la etiqueta general en la caja/pallet que contiene todos los envíos.</li>
              <li>2. Pegá cada etiqueta individual en su paquete correspondiente.</li>
              <li>3. Cuando el repartidor escanee el QR grande del bulto, los {result.bulto.count} pedidos ingresan al lote automáticamente.</li>
            </ul>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadBultoPDF} className="bg-yellow-300 text-black font-bold px-6 py-3 uppercase tracking-widest text-sm inline-flex items-center gap-2 hover:bg-yellow-200" data-testid="download-bulto-pdf">
              <Printer className="w-4 h-4" /> Descargar PDF (bulto + {result.bulto.count} etiquetas)
            </button>
            <button onClick={() => nav("/cliente/bultos")} className="border border-white/20 hover:border-yellow-300 px-6 py-3 uppercase tracking-widest text-sm">
              Ver mis bultos
            </button>
            <button onClick={() => nav("/cliente/pedidos")} className="border border-white/20 hover:border-yellow-300 px-6 py-3 uppercase tracking-widest text-sm">
              Ver pedidos
            </button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-yellow-300/40 bg-yellow-300/5">
          <div className="p-5 border-b border-yellow-300/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-yellow-300" />
              <span className="font-mono-tech text-xs uppercase tracking-widest text-yellow-300">{rows.length} filas listas para importar</span>
            </div>
            <button onClick={submit} disabled={saving} className="bg-yellow-300 text-black font-bold px-6 py-2.5 uppercase tracking-widest text-sm hover:bg-yellow-200 disabled:opacity-50" data-testid="confirm-bulk">
              {saving ? "Importando..." : `Importar ${rows.length}`}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900">
                <tr className="text-left text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500">
                  {HEADERS.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.slice(0, 20).map((r, i) => (
                  <tr key={i}>
                    {HEADERS.map((h) => <td key={h} className="px-3 py-2 text-zinc-300 whitespace-nowrap">{r[h] || "-"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 20 && <div className="p-3 text-center text-xs font-mono-tech text-zinc-500">+ {rows.length - 20} filas más...</div>}
          </div>
        </div>
      )}
    </Layout>
  );
}
